import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ExternalLink, Download, Briefcase, Clock } from "lucide-react";
import { toast } from "sonner";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export async function downloadToolboxFile(item) {
  try {
    const res = await api.get(`/toolbox/${item.id}/file`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = item.file_name || "download";
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  } catch { toast.error("Could not download the file"); }
}

function ToolCard({ item }) {
  const empty = !item.url && !item.has_file;
  return (
    <div className="bg-white rounded-2xl border border-[#dfd8da] p-6 flex flex-col" data-testid={`toolbox-item-${item.id}`}>
      <h3 className="text-lg font-bold text-[#1a1617]">{item.title}</h3>
      {item.description && <p className="mt-1 text-sm text-[#545454] flex-1">{item.description}</p>}
      <div className="mt-4 flex flex-wrap gap-2">
        {item.url && (
          <Button asChild size="sm" className="bg-[#920e12] hover:bg-[#550001] text-white" data-testid={`toolbox-open-${item.id}`}>
            <a href={item.url} target="_blank" rel="noreferrer"><ExternalLink size={14} className="mr-1" /> Open</a>
          </Button>
        )}
        {item.has_file && (
          <Button size="sm" variant="outline" onClick={() => downloadToolboxFile(item)} className="border-[#920e12] text-[#920e12]" data-testid={`toolbox-download-${item.id}`}>
            <Download size={14} className="mr-1" /> {item.file_name || "Download"}
          </Button>
        )}
        {empty && <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#545454]" data-testid={`toolbox-soon-${item.id}`}><Clock size={13} aria-hidden="true" /> Coming soon</span>}
      </div>
    </div>
  );
}

export default function Toolbox() {
  const [data, setData] = useState({ categories: [], items: [] });
  useEffect(() => { api.get("/toolbox").then(({ data }) => setData(data)).catch(() => {}); }, []);
  const groups = data.categories.map((c) => [c, data.items.filter((i) => i.category === c)]).filter(([, its]) => its.length);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="toolbox-page">
      <span className="text-xs uppercase tracking-[0.22em] font-extrabold text-[#920e12] flex items-center gap-1.5"><Briefcase size={14} aria-hidden="true" /> Directors only</span>
      <h1 className="mt-2 font-display text-4xl text-[#550001]">Director Tool Box</h1>
      <p className="mt-2 text-[#545454] max-w-2xl">Evaluations, forms, cheat sheets and promotion templates, all in one place.</p>
      {groups.map(([cat, items]) => (
        <section key={cat} className="mt-10" data-testid={`toolbox-section-${slug(cat)}`}>
          <h2 className="text-sm uppercase tracking-widest font-extrabold text-[#bf6163]">{cat}</h2>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((i) => <ToolCard key={i.id} item={i} />)}
          </div>
        </section>
      ))}
      {data.items.length === 0 && <p className="mt-10 text-[#545454]">No tools added yet.</p>}
    </div>
  );
}
